import { App, Button, Input, Spin } from 'antd';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { FileLimitationEnum } from '../../../enums/FileLimitationEnum';
import type { DecryptedFileSecretData, FileSecretData } from '../../../interfaces/FileSecret';
import type { ViewerComponentProps } from '../../../interfaces/ViewerComponentProps';
import ModalLayout from '../../../layouts/ModalLayout';
import { TransferService } from '../../../services/transferApiService';
import { decryptFileSecret } from '../../../utils/fileSecretCrypto';
import { normalizeSecretFileData, serializeSecretFileData } from '../../../utils/fileSecretPresets';
import { hasFileLimitation } from '../../../utils/utils';
import {
  FileViewerSecretEditor,
  type FileViewerSecretEditorHandle,
} from '../../Files/FileViewerSecretEditor';

const transferService = new TransferService();

export type ViewerFileSecretHandle = {
  save: () => void;
  enterEdit: () => void;
  discard: () => void;
  getMode: () => 'view' | 'edit';
};

interface Props extends ViewerComponentProps {
  /**
   * Host shell only: `modal` closes after Save + owns Close/Edit/Save footer;
   * `viewer` (page) Edit/Save live in Viewer subheader via ref + onModeChange.
   */
  variant?: 'viewer' | 'modal';
  /** Cancel / (modal) after successful Save. */
  onClose?: () => void;
  /** Notify parent when view ↔ edit changes (viewer chrome). */
  onModeChange?: (mode: 'view' | 'edit') => void;
}

const ViewerFileSecret = forwardRef<ViewerFileSecretHandle, Props>(function ViewerFileSecret(
  { file, onLoaded, variant = 'viewer', onClose, onModeChange },
  ref,
) {
  const { t } = useTranslation();
  const { message, notification } = App.useApp();
  const isModal = variant === 'modal';
  const editorRef = useRef<FileViewerSecretEditorHandle>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [fileSecretData, setFileSecretData] = useState<DecryptedFileSecretData | null>(null);
  const [isEncrypted, setIsEncrypted] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [hint, setHint] = useState<string | undefined>(undefined);
  // Open read-only; Edit unlocks fields, then Save.
  const [mode, setMode] = useState<'view' | 'edit'>('view');

  const canEdit = useMemo(() => {
    if (file.isReadOnly) return false;
    if (hasFileLimitation(file.fileLimitations, FileLimitationEnum.DenyFileEditContent))
      return false;
    return true;
  }, [file.isReadOnly, file.fileLimitations]);

  const effectiveMode: 'view' | 'edit' = canEdit ? mode : 'view';

  const setModeAndNotify = useCallback(
    (next: 'view' | 'edit') => {
      setMode(next);
      onModeChange?.(next);
    },
    [onModeChange],
  );

  const loadFileSecretData = useCallback(async () => {
    try {
      const text = await file.decryptedFileBlob.text();
      const data: FileSecretData = JSON.parse(text);

      if (data.encrypted && typeof data.fields === 'string') {
        setIsEncrypted(true);
        setHint(data.hint);
        setFileSecretData(null);
      } else {
        setFileSecretData(
          normalizeSecretFileData(
            {
              name: data.name,
              encrypted: false,
              fields: Array.isArray(data.fields) ? data.fields : [],
              hint: data.hint,
              schemaVersion: data.schemaVersion,
              preset: data.preset,
              groups: data.groups,
            },
            t,
          ),
        );
      }
    } catch {
      notification.error({
        message: t('secret.loadError'),
      });
    } finally {
      setIsLoading(false);
      onLoaded?.();
    }
  }, [file.decryptedFileBlob, onLoaded, notification, t]);

  useEffect(() => {
    loadFileSecretData();
  }, [loadFileSecretData]);

  useImperativeHandle(
    ref,
    () => ({
      save: () => {
        editorRef.current?.save();
      },
      enterEdit: () => {
        if (canEdit) setModeAndNotify('edit');
      },
      discard: () => {
        editorRef.current?.discard();
        setModeAndNotify('view');
      },
      getMode: () => effectiveMode,
    }),
    [canEdit, effectiveMode, setModeAndNotify],
  );

  const handleDecrypt = async () => {
    if (!password) return;

    try {
      const text = await file.decryptedFileBlob.text();
      const data: FileSecretData = JSON.parse(text);

      if (typeof data.fields === 'string') {
        const decryptedFieldsJson = await decryptFileSecret(data.fields, password);
        const decryptedFields = JSON.parse(decryptedFieldsJson);

        setFileSecretData(
          normalizeSecretFileData(
            {
              name: data.name,
              encrypted: true,
              fields: decryptedFields,
              hint: data.hint,
              schemaVersion: data.schemaVersion,
              preset: data.preset,
              groups: data.groups,
            },
            t,
          ),
        );
        setIsEncrypted(false);
        setPasswordError(false);
      }
    } catch {
      setPasswordError(true);
    }
  };

  const handleSave = async (
    data: DecryptedFileSecretData,
    newSecretKey?: string,
    oldSecretKey?: string,
    newHint?: string,
  ) => {
    if (isSaving) return;

    if (newSecretKey && oldSecretKey && oldSecretKey !== password) {
      notification.error({
        message: t('secret.wrongCurrentKey'),
      });
      return;
    }

    try {
      setIsSaving(true);

      const text = await file.decryptedFileBlob.text();
      const originalData: FileSecretData = JSON.parse(text);

      let fileContent: string;
      const encryptionPassword = newSecretKey || password;

      if (originalData.encrypted && encryptionPassword) {
        const fieldsJson = JSON.stringify(data.fields);
        const { encryptFileSecret } = await import('../../../utils/fileSecretCrypto');
        const encryptedFields = await encryptFileSecret(fieldsJson, encryptionPassword);
        const updatedHint = newSecretKey ? (newHint ?? hint) : hint;
        const toSave: DecryptedFileSecretData = {
          ...data,
          encrypted: true,
          hint: updatedHint,
          schemaVersion: 2,
          updatedAt: new Date().toISOString(),
        };
        fileContent = JSON.stringify(serializeSecretFileData(toSave, encryptedFields));
        if (newSecretKey) {
          setPassword(newSecretKey);
          setHint(newHint ?? hint);
        }
      } else {
        const toSave: DecryptedFileSecretData = {
          ...data,
          encrypted: false,
          schemaVersion: 2,
          updatedAt: new Date().toISOString(),
        };
        fileContent = JSON.stringify(serializeSecretFileData(toSave, data.fields));
      }

      const newFile = new File([fileContent], file.fileName, { type: 'text/plain' });

      const formData = new FormData();
      formData.append('file', newFile, file.fileName);
      formData.append('blockId', '0');
      formData.append('isLastChunk', 'true');
      formData.append('filename', file.fileName);
      formData.append('cufi', file.id.toString());

      await transferService.upload(formData);

      message.success(t('secret.saved'));
      setFileSecretData({ ...data, schemaVersion: 2 });
      setModeAndNotify('view');
      if (isModal) {
        onClose?.();
      }
    } catch {
      notification.error({
        message: t('secret.saveError'),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscardEdit = () => {
    editorRef.current?.discard();
    setModeAndNotify('view');
  };

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (isEncrypted) {
    const unlockBody = (
      <div className="flex h-full w-full flex-col items-center justify-center gap-4 p-4 sm:p-8">
        <div className="text-gray-dark text-center">
          <h2 className="mb-2 text-xl font-semibold">{t('secret.protectedFile')}</h2>
          <p>{t('secret.enterPasswordToView')}</p>
          {hint && (
            <p className="text-gray-dark mt-2 text-sm">
              {t('secret.hint')}: {hint}
            </p>
          )}
        </div>
        <div className="flex w-full max-w-sm flex-col gap-3">
          <Input.Password
            placeholder={t('secret.enterPassword')}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setPasswordError(false);
            }}
            status={passwordError ? 'error' : undefined}
            onPressEnter={handleDecrypt}
            autoFocus
          />
          {passwordError && <span className="text-red text-sm">{t('secret.wrongPassword')}</span>}
          <Button type="primary" onClick={handleDecrypt}>
            {t('secret.unlock')}
          </Button>
        </div>
      </div>
    );

    return (
      <ModalLayout maxHeight={isModal ? undefined : '100%'} className="h-full">
        <ModalLayout.Content>{unlockBody}</ModalLayout.Content>
        {isModal && onClose && (
          <ModalLayout.Footer>
            <div className="flex flex-row items-center justify-end gap-2">
              <Button size="middle" onClick={onClose}>
                {t('common.cancel')}
              </Button>
            </div>
          </ModalLayout.Footer>
        )}
      </ModalLayout>
    );
  }

  if (!fileSecretData) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <span className="text-gray-dark">{t('secret.noData')}</span>
      </div>
    );
  }

  // Modal footer + Viewer chrome own Edit/Save; editor header stays clean.
  const editor = (
    <FileViewerSecretEditor
      ref={editorRef}
      mode={effectiveMode}
      initialData={fileSecretData}
      onSave={canEdit ? handleSave : undefined}
      onModeChange={canEdit ? setModeAndNotify : undefined}
      showHeaderActions={false}
      isSaving={isSaving}
      isEncryptedFile={fileSecretData.encrypted}
    />
  );

  return (
    <ModalLayout maxHeight={isModal ? undefined : '100%'} className="h-full">
      <ModalLayout.Content>
        <div
          data-testid="viewer-file-secret"
          className={
            isModal
              ? 'mx-auto w-full max-w-3xl'
              : 'mx-auto w-full max-w-3xl px-3 pt-4 sm:px-4 sm:pt-6'
          }
        >
          {editor}
        </div>
      </ModalLayout.Content>
      {isModal && (
        <ModalLayout.Footer>
          <div className="flex flex-row items-center justify-end gap-2">
            {effectiveMode === 'view' ? (
              <>
                {onClose && (
                  <Button size="middle" onClick={onClose}>
                    {t('common.cancel')}
                  </Button>
                )}
                {canEdit && (
                  <Button size="middle" type="primary" onClick={() => setModeAndNotify('edit')}>
                    {t('secret.edit')}
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button size="middle" onClick={handleDiscardEdit} disabled={isSaving}>
                  {t('secret.cancel')}
                </Button>
                <Button
                  size="middle"
                  type="primary"
                  loading={isSaving}
                  onClick={() => editorRef.current?.save()}
                >
                  {t('common.save')}
                </Button>
              </>
            )}
          </div>
        </ModalLayout.Footer>
      )}
    </ModalLayout>
  );
});

export default ViewerFileSecret;
